/**
 * Audiobook SEO Content Component
 * 
 * Displays SEO-enhancing content sections including:
 * - Long description (rich HTML content)
 * - FAQ section
 * - Tags/Topics
 * - Related audiobooks
 * 
 * These sections improve SEO while providing value to users.
 */

'use client';

import { useState } from 'react';
import { FEATURE_FLAGS } from '@/lib/site-config';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface AudiobookSeoContentProps {
  book: {
    id: string;
    title: string;
    longDescription?: string | null;
    faqJson?: string | null;
    seoTags?: string[] | null;
    focusKeywords?: string[] | null;
  };
}

interface FAQ {
  question: string;
  answerHtml: string;
}

export function AudiobookSeoContent({ book }: AudiobookSeoContentProps) {
  // Only render if SEO enhancements are enabled
  if (!FEATURE_FLAGS.audiobookSeoEnhancements) {
    return null;
  }

  // Parse FAQ JSON
  let faqs: FAQ[] = [];
  if (book.faqJson) {
    try {
      faqs = JSON.parse(book.faqJson);
    } catch (error) {
      console.error('[SEO Content] Failed to parse FAQ JSON:', error);
    }
  }

  // Don't render if no content to display
  if (!book.longDescription && faqs.length === 0 && (!book.seoTags || book.seoTags.length === 0)) {
    return null;
  }

  return (
    <div className="seo-content-sections mt-12 mb-8 max-w-5xl mx-auto px-4">
      {/* Long Description */}
      {book.longDescription && (
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
            About "{book.title}"
          </h2>
          <div 
            className="prose prose-lg dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: book.longDescription }}
          />
        </section>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} faq={faq} />
            ))}
          </div>
        </section>
      )}

      {/* Tags/Topics */}
      {book.seoTags && book.seoTags.length > 0 && (
        <section className="mb-12">
          <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
            Topics & Themes
          </h3>
          <div className="flex flex-wrap gap-2">
            {book.seoTags.map((tag, index) => (
              <span 
                key={index} 
                className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium hover:shadow-md transition-shadow"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Related Audiobooks Placeholder */}
      <section className="mb-12">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
          You Might Also Like
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Discover more amazing audiobooks tailored for your child's age and interests.
        </p>
        {/* TODO: Implement related audiobooks logic */}
      </section>
    </div>
  );
}

/**
 * FAQ Item Component with collapsible functionality
 */
function FAQItem({ faq }: { faq: FAQ }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors text-left"
      >
        <span className="font-semibold text-gray-900 dark:text-white pr-4">
          {faq.question}
        </span>
        {isOpen ? (
          <ChevronUp className="flex-shrink-0 w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="flex-shrink-0 w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div 
          className="px-6 py-4 bg-gray-50 dark:bg-gray-900 prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: faq.answerHtml }}
        />
      )}
    </div>
  );
}

